// SPA pages share one document. Home is long; clicking through to another
// route leaves window scroll where it was. React Router 6.2 does not reset
// it. Favorite Cinema then gets a second, later jump: YouTube playVideo()
// focuses an oversized iframe and the browser scroll-into-views it, hiding
// the H1. Discrete scrollTo timers miss that. This module zeros every
// plausible vertical scroller and keeps reverting programmatic jumps until
// the visitor actually scrolls.

let pinCount = 0;
let userMoved = false;

const PAGE_SCROLLERS = [
  "html",
  "body",
  "#root",
  ".App",
  ".TheScreeningRoom",
  ".Home",
  ".TheShelf",
  "main",
];

export function disableScrollRestoration() {
  if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }
}

export function resetPageScroll() {
  if (typeof window === "undefined") return;
  disableScrollRestoration();
  window.scrollTo(0, 0);
  if (document.scrollingElement) {
    document.scrollingElement.scrollTop = 0;
  }
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  PAGE_SCROLLERS.forEach((sel) => {
    const el = document.querySelector(sel);
    if (el) el.scrollTop = 0;
  });
}

export function isPinningPageTop() {
  return pinCount > 0 && !userMoved;
}

export function holdPageAtTop(ms = 800) {
  disableScrollRestoration();
  pinCount += 1;
  userMoved = false;

  const pin = () => {
    if (pinCount <= 0 || userMoved) return;
    resetPageScroll();
  };

  // Trackpad inertia from Home can keep firing wheel events after navigate
  // and would otherwise cancel the pin before YouTube even mounts.
  let armed = false;
  const arm = setTimeout(() => {
    armed = true;
  }, 400);

  const onIntent = () => {
    if (!armed) return;
    userMoved = true;
  };

  const onScroll = () => {
    if (pinCount <= 0 || userMoved) return;
    resetPageScroll();
  };

  const onFocusIn = (e) => {
    if (pinCount <= 0 || userMoved) return;
    // Keep the iframe focused if playVideo() put it there — blur pauses some
    // YouTube embeds. Just pin the document so the overscan frame cannot
    // scroll the title away.
    resetPageScroll();
  };

  pin();
  const raf = requestAnimationFrame(() => {
    pin();
    requestAnimationFrame(pin);
  });
  const started = Date.now();
  let keep = 0;
  const tick = () => {
    if (pinCount <= 0 || userMoved) return;
    pin();
    if (Date.now() - started < ms) keep = requestAnimationFrame(tick);
  };
  keep = requestAnimationFrame(tick);

  const capture = { capture: true, passive: true };
  window.addEventListener("scroll", onScroll, true);
  document.addEventListener("scroll", onScroll, true);
  document.addEventListener("focusin", onFocusIn, true);
  window.addEventListener("wheel", onIntent, capture);
  window.addEventListener("touchmove", onIntent, capture);
  window.addEventListener("keydown", onIntent, capture);

  return () => {
    pinCount = Math.max(0, pinCount - 1);
    clearTimeout(arm);
    cancelAnimationFrame(raf);
    cancelAnimationFrame(keep);
    window.removeEventListener("scroll", onScroll, true);
    document.removeEventListener("scroll", onScroll, true);
    document.removeEventListener("focusin", onFocusIn, true);
    window.removeEventListener("wheel", onIntent, capture);
    window.removeEventListener("touchmove", onIntent, capture);
    window.removeEventListener("keydown", onIntent, capture);
  };
}
